import { Box, CircularProgress, Typography } from "@mui/material";
import { useMutation } from "convex/react";
import { ConvexError } from "convex/values";
import { ArrowRight, Check } from "lucide-react";
import React, { useId, useState } from "react";
import { api } from "../../../convex/_generated/api";
import { getDeviceInfo, getVisitorId, getVisitorLocation } from "@/lib/analyticsClient";
import { useAnalytics } from "@/hooks/useAnalytics";
import { trackClick } from "@/components/_helpers";
import { rememberSubscribedEmail } from "./mailingStorage";
import type { SubscribeOutcome } from "@/utils/mailingUnlock";

type SubscribeSource = "popup" | "release_unlock" | "artist_gate" | "footer" | "arcade_gate";

export type SubscribeRequest = {
  email: string;
  source: SubscribeSource;
  artist?: string;
  releaseKey?: string;
  pagePath?: string;
  referrer?: string;
  visitorId?: string;
  country?: string;
  deviceType?: string;
  website?: string;
};

interface SubscribeFormProps {
  source: SubscribeSource;
  artist?: string;
  releaseKey?: string;
  releaseName?: string;
  buttonLabel?: string;
  compact?: boolean;
  accentColor?: string;
  inkColor?: string;
  onSubscribed?: (outcome: SubscribeOutcome) => void;
  initialEmail?: string;
  autoFocus?: boolean;
  // Sends the signup somewhere other than api.mailingList.subscribe directly
  // (the arcade goes through its own backend so dev playtests can fake it).
  subscribeWith?: (request: SubscribeRequest) => Promise<SubscribeOutcome>;
}

export const SUBSCRIBE_FIELD_RADIUS = "10px";
export const SUBSCRIBE_STACK_MAX_WIDTH = 420;

const EVENT_BY_SOURCE: Record<SubscribeSource, string> = {
  popup: "popup_submitted",
  release_unlock: "unlock_submitted",
  artist_gate: "gate_submitted",
  footer: "footer_submitted",
  arcade_gate: "arcade_gate_submitted",
};

export function SubscribeForm({
  source,
  artist,
  releaseKey,
  releaseName,
  buttonLabel = "Send me the pack",
  compact = false,
  accentColor = "var(--logoColor)",
  inkColor = "var(--backgroundColor)",
  onSubscribed,
  initialEmail = "",
  autoFocus = false,
  subscribeWith,
}: SubscribeFormProps) {
  const subscribe = useMutation(api.mailingList.subscribe);
  const { track } = useAnalytics();
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const [email, setEmail] = useState(initialEmail);
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const showInlineError = touched && email.trim().length > 0 && !emailLooksValid;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    trackClick(`mailing-subscribe:${source}`, { label: "Mailing list submit", section: "mailing", artist, releaseKey, releaseName });
    setTouched(true);
    if (!emailLooksValid) {
      setError("Enter a valid email address");
      return;
    }
    setStatus("submitting");
    setError(null);
    try {
      const device = getDeviceInfo();
      const location = await getVisitorLocation();
      const result = await (subscribeWith ?? subscribe)({
        email: email.trim(),
        source,
        artist,
        releaseKey,
        pagePath: window.location.pathname,
        referrer: document.referrer || undefined,
        visitorId: getVisitorId(),
        country: location?.country,
        deviceType: device.deviceType,
        website,
      });
      rememberSubscribedEmail(result.email);
      track(EVENT_BY_SOURCE[source], {
        eventCategory: "mailing",
        artist,
        releaseKey,
        releaseName,
        metadata: { status: result.status, alreadySubscribed: result.alreadySubscribed, confirmationSent: result.confirmationSent },
      });
      setStatus("done");
      onSubscribed?.({ email: result.email, status: result.status, alreadySubscribed: result.alreadySubscribed, confirmationSent: result.confirmationSent });
    } catch (err) {
      // A ConvexError carries the server's own sentence (bad address, throwaway inbox).
      if (err instanceof ConvexError && typeof err.data === "string") {
        setError(err.data);
        setStatus("error");
        return;
      }
      const message = err instanceof Error ? err.message.replace(/^.*?ConvexError:\s*/, "").split("\n")[0] : "Something went wrong";
      setError(message.includes("Uncaught") ? "Something went wrong. Try again." : message);
      setStatus("error");
    }
  };

  const fieldHeight = compact ? { xs: "52px", md: "48px" } : { xs: "60px", md: "54px" };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: "100%" }}>
      <Box
        component="label"
        htmlFor={inputId}
        sx={{
          display: "block",
          fontFamily: "Poppins, sans-serif",
          fontSize: { xs: "13px", md: "0.75rem" },
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          opacity: 0.72,
          mb: "8px",
        }}
      >
        Email
      </Box>
      <Box sx={{ containerType: "inline-size", width: "100%" }}>
      <Box
        className="subscribe-row"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          gap: "8px",
          [`@container (max-width: ${SUBSCRIBE_STACK_MAX_WIDTH}px)`]: { flexDirection: "column", gap: "10px" },
        }}
      >
        <Box
          component="input"
          className="subscribe-input"
          id={inputId}
          type="email"
          name="email"
          inputMode="email"
          autoComplete="email"
          autoFocus={autoFocus}
          required
          value={email}
          disabled={status === "submitting" || status === "done"}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(event.target.value);
            if (error) setError(null);
          }}
          onBlur={() => setTouched(true)}
          placeholder="you@example.com"
          aria-invalid={showInlineError || Boolean(error)}
          aria-describedby={error || showInlineError ? errorId : undefined}
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            width: "100%",
            boxSizing: "border-box",
            height: fieldHeight,
            px: "16px",
            borderRadius: SUBSCRIBE_FIELD_RADIUS,
            border: "1px solid color-mix(in srgb, currentColor 22%, transparent)",
            backgroundColor: "color-mix(in srgb, currentColor 7%, transparent)",
            color: "inherit",
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "16px", md: "clamp(14px, 0.95rem, 17px)" },
            outline: "none",
            transition: "border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
            "&::placeholder": { color: "inherit", opacity: 0.42 },
            "&:hover:not(:disabled)": { borderColor: "color-mix(in srgb, currentColor 38%, transparent)" },
            "&:focus-visible": {
              borderColor: accentColor,
              boxShadow: `0 0 0 3px color-mix(in srgb, ${accentColor} 28%, transparent)`,
            },
            "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
            ...(showInlineError || error ? { borderColor: "#e0574f" } : {}),
          }}
        />
        <Box
          component="button"
          type="submit"
          className="subscribe-submit"
          disabled={status === "submitting" || status === "done"}
          sx={{
            flex: "0 0 auto",
            boxSizing: "border-box",
            height: fieldHeight,
            minHeight: "44px",
            px: "18px",
            borderRadius: SUBSCRIBE_FIELD_RADIUS,
            [`@container (max-width: ${SUBSCRIBE_STACK_MAX_WIDTH}px)`]: { width: "100%" },
            border: 0,
            backgroundColor: status === "done" ? "#6fbf73" : accentColor,
            color: inkColor,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 700,
            fontSize: { xs: "16px", md: "clamp(13px, 0.9rem, 16px)" },
            letterSpacing: "0.01em",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "transform 140ms cubic-bezier(0.23, 1, 0.32, 1), filter 160ms ease, background-color 200ms ease",
            "&:hover:not(:disabled)": { filter: "brightness(1.08)" },
            "&:active:not(:disabled)": { transform: "scale(0.97)" },
            "&:focus-visible": { outline: `3px solid color-mix(in srgb, ${accentColor} 55%, white)`, outlineOffset: "3px" },
            "&:disabled": { cursor: status === "done" ? "default" : "wait", opacity: status === "done" ? 1 : 0.7 },
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        >
          {status === "submitting" ? (
            <CircularProgress size={18} thickness={5} sx={{ color: "inherit" }} aria-label="Sending" />
          ) : status === "done" ? (
            <>
              <Check style={{ width: "1.15em", height: "1.15em" }} aria-hidden />
              Sent
            </>
          ) : (
            <>
              {buttonLabel}
              <ArrowRight style={{ width: "1.1em", height: "1.1em" }} aria-hidden />
            </>
          )}
        </Box>
      </Box>
      </Box>
      <Box
        component="input"
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        value={website}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setWebsite(event.target.value)}
        sx={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      {(error || showInlineError) && (
        <Typography
          id={errorId}
          role="alert"
          sx={{ mt: "8px", fontFamily: "Poppins, sans-serif", fontSize: { xs: "14px", md: "0.8rem" }, color: "#f0857e", fontWeight: 500 }}
        >
          {error || "Enter a valid email address"}
        </Typography>
      )}
    </Box>
  );
}

export default SubscribeForm;
